import * as turf from '@turf/turf'
import moment from 'moment'

import MapperIcon from '!raw-loader!@/assets/icons/mapper-icon.svg'
import ColorIcon from '!raw-loader!@/assets/icons/color-icon.svg'

export function getColorSVG(color = '#FFFFFF') {
  const parser = new DOMParser()
  const serializer = new XMLSerializer()
  const icon = parser.parseFromString(ColorIcon, "image/svg+xml")

  icon.getElementById('color-icon').setAttribute('fill', color)

  return `data:image/svg+xml;base64,${btoa(serializer.serializeToString(icon))}`
}

export function getSVG (layers, color = '#000000') {
  const parser = new DOMParser()
  const serializer = new XMLSerializer()
  const icon = parser.parseFromString(MapperIcon, "image/svg+xml")

  icon.getElementById('icon-background').setAttribute('fill', color)

  icon.querySelectorAll('[id*="-icon"]').forEach((element) => {
    element.setAttribute('style', 'visibility: hidden;')
  })

  layers.forEach(layer => {
    const element = icon.getElementById(layer)

    if (element) {
      element.setAttribute('style', null)
    }
  })

  return `data:image/svg+xml;base64,${btoa(serializer.serializeToString(icon))}`
}

export function getIcon (type, entity) {
  const icon = {
    size: {
      width: 64,
      height: 64
    },
    scaledSize: {
      width: 20,
      height: 20
    },
    anchor: {
      x: 10,
      y: 10
    },
  }

  if (type === 'Stop') {
    const stopIcon =  {
      size: {
        width: 64,
        height: 64
      },
      scaledSize: {
        width: 14,
        height: 14
      },
      anchor: {
        x: 7,
        y: 7
      },
      url: getSVG([], '#2F80ED')
    }

    if (entity.end) {
      stopIcon.url = getSVG([], 'green')
    } else if (entity && entity.serviceStop && entity.serviceStop.cancelationReason) {
      stopIcon.url = getSVG([], 'red')
    }

    return stopIcon
  } else if (type === 'CollectionLocation') {
    icon.url = getSVG(['collection-location-icon'], '#2F80ED')
    return icon
  } else if (type === 'LassoConect') {
    icon.url = getSVG([], '#EB5757')
    return icon
  } else if (type === 'PickupLocation') {
    icon.url = getSVG(['pickup-location-icon'], '#56CCF2')
    return icon
  } else if (type === 'PickupRequest') {
    if (entity.isStockpile) {
      icon.url = getSVG([
        'stockpile-icon',
        entity.haveDriverCall ? 'have-driver-call-icon' : undefined
      ], '#A0A0A0')

      return icon
    } else {
      icon.zIndex = 2000
      let color
      if (moment(entity.createdDate) > moment().subtract(7, 'days')) {
        // Created within the past 7 days
        color = '#56CCF2'
      } else if (moment(entity.createdDate) > moment().subtract(14, 'days')) {
        // Created between a week and two weeks ago
        color = '#FF681F'
      } else if (moment(entity.createdDate) > moment().subtract(21, 'days')) {
        // Created between two weeks and three weeks ago
        color = '#C96323'
      } else {
        // Created more than three weeks ago
        color = '#C62D42'
      }

      icon.url = getSVG([
        entity.isRecaptureBag ? 'recapture-bag-icon' : 'pickup-request-icon',
        entity.haveDriverCall ? 'have-driver-call-icon' : undefined
      ], color)

      return icon
    }
  }

  return undefined
}

export function toGooglePoint(point) {
  return new google.maps.LatLng(point.lat, point.lng)
}

export function toGeoJSONPoint(point) {
  return turf.point([point.lng, point.lat])
}

export function toGeoJSONPolygon(polygon) {
  return turf.polygon([
    polygon.map(point => [point.lng, point.lat])
  ])
}

export function getIntersection (polygon, locations) {
  polygon = toGeoJSONPolygon(polygon)

  const intersection = locations.filter(location => turf.booleanContains(polygon, toGeoJSONPoint(location.location.point)))

  return intersection
}

export async function calculateRoute(start, end, locations, optimizeWaypoints = true) {
  const service = new google.maps.DirectionsService()

  try {
    const route = await new Promise((resolve, reject) => {
      const request = {
        origin: toGooglePoint(start.point),
        travelMode: 'DRIVING',
        waypoints: locations.map(location => ({
          location: toGooglePoint(location.location.point),
          stopover: true
        })),
        optimizeWaypoints,
        destination: toGooglePoint(end.point)
      }

      service.route(request, (result, status) => {
        if (status === 'OK') {
          resolve(result)
        } else {
          reject(new Error(status))
        }
      })
    })

    const locationsInOrder = reorderStops(route.routes[0], locations)

    return {
      route,
      locations: locationsInOrder
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

export function reorderStops (route, stops) {
  const newStops = new Array(stops.length)

  for (const newIndex in route.waypoint_order) {
    const oldIndex = route.waypoint_order[newIndex]

    newStops[newIndex] = stops[oldIndex]
  }

  return newStops
}

export function getClosestRelativeToPoint(point, requests) {
  const geojsonPoint = toGeoJSONPoint(point)

  function distanceFromPoint(p) {
    return turf.distance(geojsonPoint, toGeoJSONPoint(p))
  }

  return requests.sort((a, b) => {
    if (distanceFromPoint(a.pickupLocation.point) > distanceFromPoint(b.pickupLocation.point)) {
      return 1
    } else {
      return -1
    }
  })
}

export function secondsToDuration(d) {
  d = Number(d);
  let h = Math.floor(d / 3600);
  let m = Math.floor(d % 3600 / 60);

  let hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hours, ") : "";
  let mDisplay = m > 0 ? m + (m == 1 ? " minute" : " minutes") : "";
  return hDisplay + mDisplay; 
}

export function secondsToTimeSpan(d) {
  d = Number(d);
  let h = Math.floor(d / 3600);
  let m = Math.floor(d % 3600 / 60);
  let s = Math.floor(d % 3600 % 60)

  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function decodePolyline(encoded){

  // array that holds the points

  var points=[ ]
  var index = 0, len = encoded.length;
  var lat = 0, lng = 0;
  while (index < len) {
    var b, shift = 0, result = 0;
    do {
      b = encoded.charAt(index++).charCodeAt(0) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    var dlat = ((result & 1) != 0 ? ~(result >> 1) : (result >> 1));
    lat += dlat;
    shift = 0;
    result = 0;

    do {
      b = encoded.charAt(index++).charCodeAt(0) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
  
    var dlng = ((result & 1) != 0 ? ~(result >> 1) : (result >> 1));
    lng += dlng;

    points.push({lat:( lat / 1E5),lng:( lng / 1E5)})  
  }

  return points
}

export function getPath (route) {
  if (route.driverPath && route.driverPath.points) {
    return route.driverPath.points
  } else if (route && route.plannedRoutePath && route.plannedRoutePath.points) {
    return route.plannedRoutePath.points
  } else {
    const points = []

    if (route.departureSite && route.departureSite.point) {
      points.push(route.departureSite.point)
    }

    if (route.Stops) {
      route.Stops.filter(stop => stop.type === 'ServiceStop')
        .forEach(stop => {
          if (stop && stop.point) {
            points.push(stop.point)
          }
        })
    }

    if (route.returnSite && route.returnSite.point) {
      points.push(route.returnSite.point)
    }

    return points
  }
}

export function getPathOptions (route) {
  if (route.driverPath && route.driverPath.points) {
    return {
      strokeColor: 'red',
      strokeWeight: 3
    }
  } else if (route && route.plannedRoutePath && route.plannedRoutePath.points) {
    const date = moment(route.scheduledDate)
    let color

    switch(date.isoWeekday()) {
      // Monday is Purple
      case 1:
        color = '#8000ff'
        break
      // Tuesday is Orange
      case 2:
        color = '#ff8000'
        break
      // Wednesday is Green
      case 3:
        color = '#009933'
        break
      // Thursday is Blue
      case 4:
        color = '#33ccff'
        break
      // Friday is Rosa
      case 5:
        color = '#ff00bf'
        break
      // Wekend is Black
      default:
        color = '#000000'
        break
    }

    return {
      strokeColor: color,
      strokeWeight: 5
    }
  } else {
    return {
      strokeColor: 'black',
      strokeWeight: 3
    }
  }
}
